/**
 * Zustand store for character data management
 * Implements character creation, update, validation, and derived statistics
 * Refactored into slices for better maintainability
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { createCharacterSlice, CharacterSlice } from './character/slices/createCharacterSlice';
import { createSkillsSlice, SkillsSlice } from './character/slices/createSkillsSlice';
import { createTalentsSlice, TalentsSlice } from './character/slices/createTalentsSlice';
import { createEquipmentSlice, EquipmentSlice } from './character/slices/createEquipmentSlice';
import {
  createAdvancementSlice,
  AdvancementSlice
} from './character/slices/createAdvancementSlice';
import {
  createProgressionSlice,
  ProgressionSlice
} from './character/slices/createProgressionSlice';
import {
  createRankSelectionSlice,
  RankSelectionSlice
} from './character/slices/createRankSelectionSlice';
import { createValidationSlice, ValidationSlice } from './character/slices/createValidationSlice';
import {
  createCombatStatesSlice,
  CombatStatesSlice
} from './character/slices/createCombatStatesSlice';
import {
  createCombatRoundSlice,
  CombatRoundSlice
} from './character/slices/createCombatRoundSlice';
import {
  createTimePassageSlice,
  TimePassageSlice
} from './character/slices/createTimePassageSlice';

// Re-export selectors
export { useSelectors, useCharacterProficiencies } from './character/selectors';
export {
  createDefaultProgression,
  calculateWoundsMaximum,
  calculateMovementRates,
  migrateCharacterToProgression
} from './character/utils/calculations';

// Combined store interface
export interface CharacterState
  extends CharacterSlice,
    SkillsSlice,
    TalentsSlice,
    EquipmentSlice,
    AdvancementSlice,
    ProgressionSlice,
    RankSelectionSlice,
    ValidationSlice,
    CombatStatesSlice,
    CombatRoundSlice,
    TimePassageSlice {}

export const useCharacterStore = create<CharacterState>()(
  subscribeWithSelector((...a) => ({
    ...createCharacterSlice(...a),
    ...createSkillsSlice(...a),
    ...createTalentsSlice(...a),
    ...createEquipmentSlice(...a),
    ...createAdvancementSlice(...a),
    ...createProgressionSlice(...a),
    ...createRankSelectionSlice(...a),
    ...createValidationSlice(...a),
    ...createCombatStatesSlice(...a),
    ...createCombatRoundSlice(...a),
    ...createTimePassageSlice(...a)
  }))
);
